import { AiChatMessage, AiChatResponse } from "@app/projects/network-assessment/provider-hub/interfaces";
import ChatbotService from "@app/projects/network-assessment/provider-hub/ai-assistant/chatbotService";

/** How long the POST gets on its own before we start reading the answer back. Typical answers land well under 40s. */
export const POST_WATCHDOG_MS = 45_000;

/** Gap between read-back attempts once the POST has failed or stalled. */
export const POLL_INTERVAL_MS = 10_000;

/**
 * How many read-back attempts before we declare the turn lost (~20s of polling, ~65s total with the
 * watchdog). This backstop should almost never fire, so we favor fewer full-thread refetches over
 * finer-grained detection of exactly when the answer landed.
 */
export const POLL_ATTEMPTS = 2;

/** The subset of ChatbotService the read-back needs. */
export type ConversationReader = Pick<ChatbotService, 'getConversation'>;

export type TurnOutcome =
/** The POST delivered normally; append `response.messages` to the outgoing list. */
  | { kind: 'answered'; response: AiChatResponse }
  /** The POST did not deliver but the answer was read back; replace the message list with the thread. */
  | { kind: 'recovered'; conversation: AiChatResponse }
  /** Neither channel produced an answer. */
  | { kind: 'lost' };

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Picks the message a recovered (or freshly re-read) conversation contributed for this turn.
 * ResponseId is a SQL identity, so a message with a greater id than `sinceResponseId` was necessarily
 * written after this turn was submitted. Older messages — including the thread's oldest prompt — are
 * ignored so callers don't misreport a field (e.g. ExcludeFromPromptLimits) from the wrong message.
 */
export const findLandedMessage = (
  messages: AiChatMessage[],
  sinceResponseId: number
): AiChatMessage | undefined =>
  messages.find(message => (message.responseId ?? 0) > sinceResponseId);

/**
 * Waits for the chat POST, and if it fails or hangs, reads the persisted thread back instead.
 *
 * The answer is written to SQL before the POST returns, so a dropped or stalled connection loses the
 * delivery but not the data. The POST is deliberately never aborted: the API passes
 * HttpContext.RequestAborted into the workflow, and cancelling on the client only stops the browser from
 * waiting — it doesn't guarantee the server keeps generating. This backstop reliably recovers the case
 * where the server finishes and writes the row but the response never makes it back to the browser (e.g.
 * a dropped connection or a proxy that truncates the reply after the upstream call already completed).
 *
 * @param chatbotService The service that makes the POST call.
 * @param post The original POST request.
 * @param threadId The applicable thread ID for the conversation to retrieve.
 * @param sinceResponseId The highest responseId already known to the caller. A message with a greater
 * id was necessarily written after this turn was submitted, since ResponseId is a SQL identity.
 */
export const waitForAnswer = async (
  chatbotService: ConversationReader,
  post: Promise<AiChatResponse>,
  threadId: string,
  sinceResponseId: number
): Promise<TurnOutcome> => {
  // Resolve to null rather than rejecting so a dead POST falls straight through to the read-back
  // instead of waiting out the watchdog.
  const delivered = await Promise.race([post.catch((): null => null), wait(POST_WATCHDOG_MS)]);
  if (delivered) {
    return { kind: 'answered', response: delivered };
  }

  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    await wait(POLL_INTERVAL_MS);
    // A failed read-back is transient — keep trying until the attempts are spent.
    const conversation = await chatbotService.getConversation(threadId).catch((): null => null);
    if (conversation?.messages && !!findLandedMessage(conversation.messages, sinceResponseId)) {
      return { conversation, kind: 'recovered' };
    }
  }

  return { kind: 'lost' };
};
